/* eslint-disable max-len */
import {
    ReactNode, FunctionComponent, MutableRefObject,
} from 'react'
import {
    GridApiPro, GridCallbackDetails,
    GridColDef, GridColumnVisibilityModel,
    GridDataSource, GridFilterItem, GridFilterModel,
    GridGetRowsError, GridRenderCellParams,
    GridRowSelectionModel, GridSlotProps, GridSlotsComponent,
    GridUpdateRowError, GridRowId,
    GridSortModel, GridColumnDimensions, GridRowParams,
    GridPaginationModel,
} from '@mui/x-data-grid-pro'
// TODO-DK: imports from other modules, disconnect it in https://skycell.atlassian.net/browse/IP-23275
import AllCellComponentsSpecialProps from 'app/common/DomainObject/ComponentMapper'
import {
    RequestKeys,
} from 'app/hooks/useRequest'

import {
    FilterType,
} from 'app/enums/common.enums'
import {
    RequestConfig,
} from 'app/types/request.types'
import {
    SkyBaseDataGridToolbarProps,
} from './components/SkyBaseDataGridToolbar/SkyBaseDataGridToolbar.types'

// TODO-DK: this is business logic related, move out from here in https://skycell.atlassian.net/browse/IP-23275
/*  Define features that can be configurable for New Tables */
export enum DataGridSupportedFeatures {
    ShowArchived = 'showArchived',
    BulkCreate = 'bulkCreate', // not supported yet
    BulkEdit = 'bulkEdit', // not supported yet
}

/* new Filters types that will be supported only in new Tables */
export enum SkyNetDataGridFilterType {
    Boolean = 'boolean',
    Toggle = 'toggle',
    DateRange = 'date-range',
}

export type ExtendedFilterType = FilterType | SkyNetDataGridFilterType

type CellRenderableColumnProps =
    { renderCell?: (props: GridRenderCellParams) => JSX.Element } | AllCellComponentsSpecialProps

type FilterableDataGridColumnProps = {
    filterField?: string,
    filterType?: ExtendedFilterType,
    filterable?: boolean,
    filterParams?: {
        defaultValue?: string | number | boolean | Array<string | number | boolean>,
        operator?: string,
    }
    filterDateMask?: string, /* used for custom date format in date range filter. Default value is DD.MM.YYYY HH:mm ZZ */
}

/* New Type for defining columns in SkyDataGrid */
export type SkyDataGridColumnDefinition<T = {}> = {
    name: keyof T,
    labelKey?: string,
    reduced?: boolean,
    customFilterItemLabelConverter?: (value: any) => string,
    width?: number,
    minWidth?: number,
    maxWidth?: number,
    sorting?: boolean,
    hiddenFromColumns?: boolean, /* if true - column wouldn't be visible but will be added to FilterMenu */
    headerName?: string,
} & FilterableDataGridColumnProps & CellRenderableColumnProps

export type ExtendedGridApiPro = GridApiPro & {
    goToPage?: (e: unknown, page: number) => void,
    resetToFirstPage?: () => void,
    updateGridDimensions?: () => void
}

/*  DataSource for DataGrid with Error handling */
export type SkyBaseDataSourceProvider = {
    dataSource: GridDataSource,
    onDataSourceError: (rowsError: (GridGetRowsError | GridUpdateRowError)) => void,
    errorMessage?: string,
    showArchived?: boolean,
    key?: RequestKeys | string
    request?: RequestConfig,
    invalidate?: () => void,
}

/* Our custom filterOperators keys for FilterModer */
export enum FilterOperatorType {
    EqualsIncludeFilter = 'equalsIncludeFilter', // for includeFilters
    // for other filters that will be added to request body , like { archived: true }
    DataFilter = 'dataFilter',
}

export type ExtendedGridColDef = GridColDef & SkyDataGridColumnDefinition<any>

export type ExtendedGridFilterItem = GridFilterItem & {
    defaultValue?: string | number | boolean | Array<string | number | boolean>,
}
export type ExtendedGridFilterModel = GridFilterModel & {
    items: ExtendedGridFilterItem[]
}

export type DetailPanelProps = {
    getDetailPanelHeight?: (params: GridRowParams) => number | 'auto',
    getDetailPanelContent?: (params: GridRowParams) => ReactNode,
    detailPanelExpandedRowIds?: GridRowId[],
    onDetailPanelExpandedRowIdsChange?: (ids: Set<GridRowId>, details: GridCallbackDetails) => void
}

export type SkyBaseDataGridProps = {
    persistentKey: string,
    gridApiRef?: MutableRefObject<GridApiPro>,
    columns: ExtendedGridColDef[],
    customDataSource: SkyBaseDataSourceProvider,
    rowHeight?: number | 'auto',
    height?: string | number,
    onRowClick?: (params: any) => void,
    rowSelectionModel?: GridRowSelectionModel,
    onRowSelectionModelChange?: (newRowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails) => void,
    tableWidthPercent?: number,
    initialTableWidthPercent?: number,
    showToolbar?: boolean,
    slots?: Partial<GridSlotsComponent>,
    slotProps?: GridSlotProps,
    classNames?: {
        container?: string,
        tableWrapper?: string,
    },
    visibilityModel: GridColumnVisibilityModel,
    onColumnVisibilityModelChange: (model: GridColumnVisibilityModel, wasSetByUser?: boolean) => void,
    Pagination?: FunctionComponent,
    paginationModel?: GridPaginationModel,
    onPaginationModelChange?: (model: GridPaginationModel) => void,
    CustomToolbar?: FunctionComponent
    toolbarProps?: SkyBaseDataGridToolbarProps
    CustomFooter?: FunctionComponent,
    customColumnsSetStateConverter?: (value: SkyDataGridColumnsState, oldState?: SkyDataGridLocalStorage) => SkyDataGridLocalStorage,
    customColumnsInitStateConverter?: (dataGridState: SkyDataGridLocalStorage) => SkyDataGridColumnsState,
    enableSortFilterPersistence?: boolean,
    selectionModelType?: 'include' | 'exclude',
    checkboxSelection?: boolean,
    disableMultipleRowSelection?: boolean,
    disableRowSelectionOnClick?: boolean,
    disabled?: boolean,
    getRowId?: (row: any) => string | number,
} & DetailPanelProps

export type SkyDataGridColumnsState = {
    orderedFields?: string[],
    dimensions?: Record<string, GridColumnDimensions>,
}

/* State that can be persisted separately for specific view */
export type SkyDataGridCustomViewState = {
    columns?: SkyDataGridColumnsState,
    visibilityModel?: GridColumnVisibilityModel,
}

export type SkyDataGridDetailViewState = SkyDataGridCustomViewState & {
    visibilityModelByResize?: GridColumnVisibilityModel,
}

export type SkyDataGridLocalStorage = SkyDataGridCustomViewState & {
    filterModel?: GridFilterModel,
    sortModel?: GridSortModel,
    tableWidthPercent?: number,
    detailsView?: SkyDataGridDetailViewState,
}
